import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { onSessionEnded } from '../api';

import { sessionKeys } from './query-keys';
import { currentUserIdOf, sessionQueryOptions } from './queryOptions';
import { forgetCachedData, isCurrentSessionQuery, replaceSession } from './sessionCache';
import { onOtherTabs, tellOtherTabs } from './sessionEvents';
import { SIGNED_OUT } from './types';

const reloadPage = (): void => window.location.reload();

const nothingMore = (): void => undefined;

export interface SessionSyncOptions {
  /** Client state outside the query cache that features keep for the signed-in person, dropped when they leave. */
  readonly onPersonLeft?: () => void;
  /** What a previous person may have left on this device, dropped whenever the tab finds no one signed in. */
  readonly onNoOneSignedIn?: () => void;
  readonly reload?: () => void;
}

/**
 * Keeps this tab's session in step with the browser's, and never lets one person's
 * screens run on another's data:
 * - whenever the person leaves — a sign-out here or in another tab, a lost session,
 *   a fresh read that finds it over — everything held for them is forgotten;
 * - whenever a fresh read finds someone else signed in, the tab starts afresh;
 * - a sign-in in another tab, or a page restored from the back-forward cache, asks
 *   the server again. A sign-in made in this tab is its own and changes nothing else.
 */
export const useSessionSync = ({
  onPersonLeft = nothingMore,
  onNoOneSignedIn = nothingMore,
  reload = reloadPage,
}: SessionSyncOptions = {}): void => {
  const queryClient = useQueryClient();
  useEffect(() => {
    let held = currentUserIdOf(queryClient);
    // The server may have answered before this tab started following the session.
    if (queryClient.getQueryData(sessionQueryOptions().queryKey)?.status === 'signedOut') onNoOneSignedIn();
    const stopWatching = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success' || !isCurrentSessionQuery(event.query)) return;
      const now = currentUserIdOf(queryClient);
      if (now === held) {
        if (now === null) onNoOneSignedIn();
        return;
      }
      const previous = held;
      held = now;
      if (now === null) {
        forgetCachedData(queryClient);
        onPersonLeft();
      } else if (previous !== null && event.action.manual !== true) {
        reload();
      }
    });
    const readAgain = (): void => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    };
    const stopOtherTabs = onOtherTabs((message) => {
      if (message.type === 'signedOut') replaceSession(queryClient, SIGNED_OUT);
      else readAgain();
    });
    const stopEnded = onSessionEnded(() => {
      replaceSession(queryClient, SIGNED_OUT);
      tellOtherTabs({ type: 'signedOut' });
    });
    const onShow = (event: PageTransitionEvent): void => {
      if (event.persisted) readAgain();
    };
    window.addEventListener('pageshow', onShow);
    return () => {
      stopWatching();
      stopOtherTabs();
      stopEnded();
      window.removeEventListener('pageshow', onShow);
    };
  }, [queryClient, onPersonLeft, onNoOneSignedIn, reload]);
};

interface SessionSyncProps {
  readonly onPersonLeft?: () => void;
  readonly onNoOneSignedIn?: () => void;
}

export const SessionSync = ({ onPersonLeft, onNoOneSignedIn }: SessionSyncProps): null => {
  useSessionSync({ onPersonLeft, onNoOneSignedIn });
  return null;
};
